import { Link } from 'react-router-dom'
import { Settings } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { useAuth } from '@/hooks/use-auth'
import { useTabQueryParam } from '@/hooks/use-tab-query-param'
import { cn } from '@/lib/utils'
import { BillingCyclesTab } from './billing-cycles-tab'
import { InvoiceListTab } from './invoice-list-tab'
import { CreditNotesListTab } from './credit-notes-list-tab'
import { ArrearsReportTab } from './arrears-report-tab'

type Tab = 'by-period' | 'invoices' | 'credit-notes' | 'arrears'
const TABS: { id: Tab; label: string }[] = [
  { id: 'by-period', label: 'By period' },
  { id: 'invoices', label: 'All invoices' },
  { id: 'credit-notes', label: 'Credit Notes' },
  { id: 'arrears', label: 'Arrears' },
]

const VALID_TABS: Tab[] = ['by-period', 'invoices', 'credit-notes', 'arrears']

export function BillingWorkspace({ basePath }: { basePath: string }) {
  const { user } = useAuth()
  // Deep-linkable (e.g. the Payments Overview's arrears link) and survives a refresh.
  const [tab, setTab] = useTabQueryParam(VALID_TABS, 'by-period')

  return (
    <PageShell>
      <PageHeader
        label="Finance"
        title="Billing"
        description="Browse billing cycles by utility type, or search invoices, credit notes, and arrears."
        action={
          user?.role === 'admin' && (
            <Link
              to="/admin/utility-types"
              className="inline-flex h-8 items-center justify-center gap-1.5 rounded border border-border bg-transparent px-3.5 font-sans text-xs font-medium tracking-wide text-ink transition-colors hover:border-ink"
            >
              <Settings size={14} />
              Manage utility types
            </Link>
          )
        }
      />

      <div className="flex gap-1 border-b border-border-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-4 py-2.5 font-sans text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-b-2 border-primary text-ink'
                : 'text-muted-foreground hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'by-period' && <BillingCyclesTab basePath={basePath} />}
      {tab === 'invoices' && <InvoiceListTab />}
      {tab === 'credit-notes' && <CreditNotesListTab />}
      {tab === 'arrears' && <ArrearsReportTab />}
    </PageShell>
  )
}
